/**
 * Route switch and document-title management.
 *
 * The title and the focus move are handled here rather than in each view
 * because they are the two things a single-page navigation silently breaks. On
 * a client-side navigation the document title is set and the main region takes
 * focus, so a keyboard or screen-reader user lands at the top of the new page
 * instead of wherever the previous page's DOM left them.
 */

import { type ReactNode, useEffect, useRef } from "react";
import { useStorefront } from "./app/StorefrontContext.js";
import { useAnnounce } from "./components/Announcer.js";
import { Shell } from "./components/Shell.js";
import { NotFoundBlock } from "./components/states.js";
import { AccountView } from "./features/AccountView.js";
import { CartView } from "./features/CartView.js";
import { CatalogView } from "./features/CatalogView.js";
import { CheckoutView } from "./features/CheckoutView.js";
import { ConfirmationView } from "./features/ConfirmationView.js";
import { InlineInquiry } from "./features/InquiryForm.js";
import { ItemView } from "./features/ItemView.js";
import { PoliciesView } from "./features/PoliciesView.js";
import { ProductView } from "./features/ProductView.js";
import { routeTitle } from "./lib/routes.js";

export function App(): ReactNode {
  const { route, config, preload } = useStorefront();
  const announce = useAnnounce();
  const firstRender = useRef(true);

  useEffect(() => {
    const itemTitle =
      route.name === "item" &&
      preload?.item?.physical_item_id === route.itemID
        ? preload.item.title
        : null;
    const title =
      itemTitle === null ? routeTitle(route, config.project_name) : `${itemTitle} · ${config.project_name}`;
    document.title = title;
    if (firstRender.current) {
      // The server already rendered this route; announcing it would talk over
      // the page a screen reader is only just starting to read.
      firstRender.current = false;
      return;
    }
    announce(title);
    document.getElementById("main")?.focus();
  }, [route, config.project_name, preload, announce]);

  return (
    <Shell>
      <RouteView />
    </Shell>
  );
}

function RouteView(): ReactNode {
  const { route } = useStorefront();
  switch (route.name) {
    case "catalog":
      return (
        <CatalogView
          offset={route.offset}
          platform={route.platform}
          region={route.region}
          search={route.search}
        />
      );
    case "product":
      return <ProductView productID={route.productID} />;
    case "item":
      return <ItemView itemID={route.itemID} />;
    case "cart":
      return <CartView />;
    case "checkout":
      return <CheckoutView />;
    case "checkout-return":
      return <ConfirmationView orderID={route.orderID} />;
    case "account":
      return <AccountView />;
    case "help":
      return (
        <div className="stack">
          <h1 className="t-display-l">{route.orderID === undefined ? "Ask about an item" : "Help with your order"}</h1>
          <InlineInquiry
            {...(route.orderID === undefined ? {} : { orderID: route.orderID })}
            heading="Send us a message"
            intro="Questions about a grade, a certification number, the slab condition, shipping, or an order in progress all reach the same place."
          />
        </div>
      );
    case "policies":
      return <PoliciesView />;
    case "not-found":
      return (
        <div className="stack">
          <h1 className="t-display-l">Page not found</h1>
          <NotFoundBlock what="page" />
        </div>
      );
  }
}
